import { useSuspenseQuery } from '@tanstack/react-query'
import { ChevronRightIcon } from 'lucide-react'
import { Suspense, useMemo, useState } from 'react'

import { employersEmployees } from 'rails_routes'
import { ButtonLink } from 'slab'
import { getEmployersEnrollmentsCounts, getGetEmployersEnrollmentsCountsQueryKey } from 'utils/web_api'
import { type EmployersEnrollmentStatusCounts } from 'utils/web_api.schemas'

import EmptyState from './EmptyState'
import EnrollmentsLegendItems from './EnrollmentStatusLegendItems'
import StackedBarChart from './StackedBarChart'

export type DisplayedEnrollmentStatus = keyof Omit<EmployersEnrollmentStatusCounts, 'total_count' | 'other'>

export const ENROLLMENT_STATUS_ORDER: DisplayedEnrollmentStatus[] = [
  'enrolled',
  'viewing_plans',
  'invite_sent',
  'declined',
  'not_invited',
]

export const ENROLLMENT_STATUS_DISPLAY_DICT: Record<DisplayedEnrollmentStatus, string> = {
  not_invited: 'Not invited yet',
  enrolled: 'Enrolled',
  viewing_plans: 'Viewing plans',
  invite_sent: 'Invite sent',
  declined: 'Declined',
}

export const ENROLLMENT_STATUS_STYLE: Record<DisplayedEnrollmentStatus, React.CSSProperties> = {
  not_invited: {
    background: 'white',
    border: '1px solid #919191',
  },
  enrolled: {
    backgroundColor: '#29845A',
  },
  viewing_plans: {
    background: 'repeating-linear-gradient(-45deg, #29845A 0, #29845A 1px, white 1px, white 2px)',
    border: '1px solid #29845A',
  },
  invite_sent: {
    backgroundColor: 'white',
    border: '1px solid #29845A',
  },
  declined: {
    backgroundColor: '#919191',
  },
}

function EnrollmentTrackerComponent ({
  enrollmentStatusCounts,
  totalNumEmployeesInvited,
  hoveredStatus,
  setHoveredStatus,
  isLoading,
}: {
  enrollmentStatusCounts?: EmployersEnrollmentStatusCounts;
  totalNumEmployeesInvited: number;
  hoveredStatus: DisplayedEnrollmentStatus | null;
  setHoveredStatus: (status: DisplayedEnrollmentStatus | null) => void;
  isLoading: boolean;
}) {
  return (
    <div className='flex flex-col gap-6 flex-1'>
      <p className='body text-gray-500'>Enrollment rate</p>
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2'>
          <div className='flex items-end gap-4'>
            <p className='heading-md text-gray-800'>
              {enrollmentStatusCounts
                ? `${totalNumEmployeesInvited > 0 ? Math.round((enrollmentStatusCounts.enrolled / totalNumEmployeesInvited) * 100) : 0}%`
                : <span className='inline-block w-6 h-7 rounded-md bg-gray-200 animate-pulse align-middle' />}
            </p>
            <p className='body-sm text-gray-500 pb-0.5'>
              {enrollmentStatusCounts
                ? `${enrollmentStatusCounts.enrolled}/${totalNumEmployeesInvited} eligible employees enrolled`
                : <span className='inline-block w-24 h-4 rounded-md bg-gray-200 animate-pulse align-middle' />}
            </p>
          </div>
          <StackedBarChart
            enrollmentStatusCounts={enrollmentStatusCounts}
            isLoading={isLoading}
            totalNumEmployeesInvited={totalNumEmployeesInvited}
            hoveredStatus={hoveredStatus}
            setHoveredStatus={setHoveredStatus}
          />
        </div>
        <EnrollmentsLegendItems
          enrollmentStatusCounts={enrollmentStatusCounts}
          isLoading={isLoading}
          hoveredStatus={hoveredStatus}
          setHoveredStatus={setHoveredStatus}
        />
      </div>
      <div className='flex justify-end -mr-4'>
        <ButtonLink
          href={employersEmployees.index()}
          variant='tertiary-deemphasized'
          rightIcon={ChevronRightIcon}
        >
          See all employees
        </ButtonLink>
      </div>
    </div>
  )
}

function EnrollmentTrackerWithData () {
  const { data: enrollmentStatusCounts } = useSuspenseQuery({
    queryKey: getGetEmployersEnrollmentsCountsQueryKey(),
    queryFn: () => getEmployersEnrollmentsCounts(),
  })

  const [hoveredStatus, setHoveredStatus] = useState<DisplayedEnrollmentStatus | null>(null)

  const totalNumEmployeesInvited = useMemo(() => {
    if (!enrollmentStatusCounts) return 0
    return (
      enrollmentStatusCounts.invite_sent +
      enrollmentStatusCounts.declined +
      enrollmentStatusCounts.viewing_plans +
      enrollmentStatusCounts.enrolled
    )
  }, [enrollmentStatusCounts])

  if (totalNumEmployeesInvited === 0) {
    return <EmptyState />
  }

  return (
    <EnrollmentTrackerComponent
      enrollmentStatusCounts={enrollmentStatusCounts}
      totalNumEmployeesInvited={totalNumEmployeesInvited}
      hoveredStatus={hoveredStatus}
      setHoveredStatus={setHoveredStatus}
      isLoading={false}
    />
  )
}

export default function EnrollmentTracker () {
  const fallback = (
    <EnrollmentTrackerComponent
      totalNumEmployeesInvited={0}
      hoveredStatus={null}
      setHoveredStatus={() => {}}
      isLoading
    />
  )

  return (
    <Suspense fallback={fallback}>
      <EnrollmentTrackerWithData />
    </Suspense>
  )
}
